import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

interface EvidenceScenarioDefinition {
  id: string;
  label: string;
  expectedOutcome: string;
}

interface KsefOutgoingTestEvidenceEntry {
  scenarioId: string;
  expectedOutcome: string;
  actualOutcome: string;
  invoiceId: string;
  correlationId: string;
  finalStatus: string;
  passFail: 'pass' | 'fail';
  note: string;
  createdAtUtc: string;
}

interface KsefOutgoingTestEvidencePanelProps {
  selectedInvoiceId: string;
  lastActionLabel: string;
  lastActionResult: string;
  correlationId?: string;
  finalStatus?: string;
}

interface EvidenceFormState {
  scenarioId: string;
  expectedOutcome: string;
  actualOutcome: string;
  correlationId: string;
  finalStatus: string;
  passFail: 'pass' | 'fail';
  note: string;
}

type ScenarioChecklistStatus = 'notStarted' | 'pass' | 'fail';

const checklistStatusLabel = (status: ScenarioChecklistStatus): string => {
  if (status === 'notStarted') {
    return 'nie rozpoczęto';
  }

  if (status === 'pass') {
    return 'zaliczony';
  }

  return 'niezaliczony';
};

const passFailLabel = (status: 'pass' | 'fail'): string => {
  return status === 'pass' ? 'zaliczony' : 'niezaliczony';
};

const scenarioDefinitions: EvidenceScenarioDefinition[] = [
  {
    id: 'S1_ELIGIBLE_AUTO_HAPPY_PATH',
    label: 'EligibleAuto happy path',
    expectedOutcome: 'prepare -> submit -> poll accepted -> upo url 200',
  },
  {
    id: 'S2_MANUAL_ONLY_WITH_DECISION',
    label: 'ManualOnly with operator decision',
    expectedOutcome: 'manual decision visible and flow completes after explicit operator path',
  },
  {
    id: 'S3_BLOCKED_HARD',
    label: 'BlockedHard',
    expectedOutcome:
      'submit path is blocked by policy or validation and returns deterministic error',
  },
  {
    id: 'S4_IDEMPOTENT_SUBMIT',
    label: 'Idempotent submit',
    expectedOutcome: 'repeated submit returns consistent outcome without duplicate side effects',
  },
  {
    id: 'S5_REJECTED_PATH',
    label: 'Rejected terminal path',
    expectedOutcome: 'terminal rejected state is visible and no UPO is expected',
  },
  {
    id: 'S6_TRANSIENT_POLL_FAILURE',
    label: 'Transient poll failure',
    expectedOutcome:
      'non-terminal controlled failure is visible and retry-required class is explicit',
  },
  {
    id: 'S7_ACCEPTED_DELAYED_UPO',
    label: 'Accepted with delayed UPO retrieval',
    expectedOutcome: 'accepted state appears first and UPO URL becomes available later',
  },
  {
    id: 'S8_AUTH_FRESHNESS',
    label: 'Authentication freshness',
    expectedOutcome: 'expired auth path is handled and operator recovery via re-login is validated',
  },
];

const defaultScenario = scenarioDefinitions[0];

const escapeCsv = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const downloadFile = (content: string, fileName: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const KsefOutgoingTestEvidencePanel = ({
  selectedInvoiceId,
  lastActionLabel,
  lastActionResult,
  correlationId,
  finalStatus,
}: KsefOutgoingTestEvidencePanelProps) => {
  const [entries, setEntries] = useState<KsefOutgoingTestEvidenceEntry[]>([]);
  const [form, setForm] = useState<EvidenceFormState>({
    scenarioId: defaultScenario.id,
    expectedOutcome: defaultScenario.expectedOutcome,
    actualOutcome: '',
    correlationId: correlationId ?? '',
    finalStatus: finalStatus ?? '',
    passFail: 'pass',
    note: '',
  });

  const scenarioMap = useMemo(() => {
    return Object.fromEntries(scenarioDefinitions.map((scenario) => [scenario.id, scenario]));
  }, []);

  const latestEntryByScenario = useMemo(() => {
    const map = new Map<string, KsefOutgoingTestEvidenceEntry>();

    for (const entry of entries) {
      if (!map.has(entry.scenarioId)) {
        map.set(entry.scenarioId, entry);
      }
    }

    return map;
  }, [entries]);

  const scenarioChecklist = useMemo(() => {
    return scenarioDefinitions.map((scenario) => {
      const lastEntry = latestEntryByScenario.get(scenario.id);

      const status: ScenarioChecklistStatus = !lastEntry
        ? 'notStarted'
        : lastEntry.passFail === 'pass'
          ? 'pass'
          : 'fail';

      return {
        ...scenario,
        status,
        lastUpdatedAtUtc: lastEntry?.createdAtUtc ?? '-',
      };
    });
  }, [latestEntryByScenario]);

  const checklistCoverage = useMemo(() => {
    const completed = scenarioChecklist.filter((item) => item.status !== 'notStarted').length;
    const passed = scenarioChecklist.filter((item) => item.status === 'pass').length;
    const failed = scenarioChecklist.filter((item) => item.status === 'fail').length;

    return {
      total: scenarioChecklist.length,
      completed,
      passed,
      failed,
    };
  }, [scenarioChecklist]);

  const applyScenario = (scenarioId: string): void => {
    const scenario = scenarioMap[scenarioId];
    setForm((prev) => ({
      ...prev,
      scenarioId,
      expectedOutcome: scenario?.expectedOutcome ?? prev.expectedOutcome,
    }));
  };

  const captureLatestResult = (): void => {
    setForm((prev) => ({
      ...prev,
      actualOutcome: [lastActionLabel, lastActionResult].filter(Boolean).join('\n\n'),
      correlationId: correlationId ?? prev.correlationId,
      finalStatus: finalStatus ?? prev.finalStatus,
    }));
  };

  const addEntry = (): void => {
    if (!form.scenarioId || !form.expectedOutcome || !form.actualOutcome) {
      return;
    }

    const entry: KsefOutgoingTestEvidenceEntry = {
      scenarioId: form.scenarioId,
      expectedOutcome: form.expectedOutcome,
      actualOutcome: form.actualOutcome,
      invoiceId: selectedInvoiceId,
      correlationId: form.correlationId,
      finalStatus: form.finalStatus,
      passFail: form.passFail,
      note: form.note,
      createdAtUtc: new Date().toISOString(),
    };

    setEntries((prev) => [entry, ...prev]);
  };

  const exportJson = (): void => {
    const payload = {
      exportedAtUtc: new Date().toISOString(),
      entries,
    };

    downloadFile(
      JSON.stringify(payload, null, 2),
      `ksef-mode-c-evidence-${Date.now()}.json`,
      'application/json'
    );
  };

  const exportCsv = (): void => {
    const header = [
      'createdAtUtc',
      'scenarioId',
      'invoiceId',
      'correlationId',
      'finalStatus',
      'passFail',
      'expectedOutcome',
      'actualOutcome',
      'note',
    ];

    const rows = entries.map((entry) =>
      [
        entry.createdAtUtc,
        entry.scenarioId,
        entry.invoiceId,
        entry.correlationId,
        entry.finalStatus,
        entry.passFail,
        entry.expectedOutcome,
        entry.actualOutcome,
        entry.note,
      ]
        .map((value) => escapeCsv(value ?? ''))
        .join(',')
    );

    downloadFile(
      [header.join(','), ...rows].join('\n'),
      `ksef-mode-c-evidence-${Date.now()}.csv`,
      'text/csv;charset=utf-8'
    );
  };

  const getChecklistChipColor = (
    status: ScenarioChecklistStatus
  ): 'default' | 'success' | 'warning' | 'error' => {
    if (status === 'pass') {
      return 'success';
    }

    if (status === 'fail') {
      return 'error';
    }

    return 'warning';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Dowody testowe</Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              label="Scenariusz"
              fullWidth
              value={form.scenarioId}
              onChange={(event) => applyScenario(event.target.value)}
            >
              {scenarioDefinitions.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>
                  {scenario.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="ID faktury"
              fullWidth
              value={selectedInvoiceId}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              label="Wynik"
              fullWidth
              value={form.passFail}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  passFail: event.target.value as EvidenceFormState['passFail'],
                }))
              }
            >
              <MenuItem value="pass">zaliczony</MenuItem>
              <MenuItem value="fail">niezaliczony</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Oczekiwany rezultat"
              fullWidth
              value={form.expectedOutcome}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  expectedOutcome: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="ID korelacji"
              fullWidth
              value={form.correlationId}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  correlationId: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Status końcowy"
              fullWidth
              value={form.finalStatus}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  finalStatus: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Rzeczywisty rezultat"
              fullWidth
              multiline
              minRows={4}
              value={form.actualOutcome}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  actualOutcome: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notatka"
              fullWidth
              value={form.note}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  note: event.target.value,
                }))
              }
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={captureLatestResult}>
            Pobierz ostatni wynik
          </Button>
          <Button variant="contained" onClick={addEntry}>
            Dodaj wpis dowodowy
          </Button>
          <Button variant="outlined" onClick={exportJson} disabled={entries.length === 0}>
            Eksportuj JSON
          </Button>
          <Button variant="outlined" onClick={exportCsv} disabled={entries.length === 0}>
            Eksportuj CSV
          </Button>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Chip label={`Wpisy: ${entries.length}`} />
          <Chip
            label={`Scenariusze wykonane: ${checklistCoverage.completed}/${checklistCoverage.total}`}
          />
          <Chip color="success" label={`Zaliczone: ${checklistCoverage.passed}`} />
          <Chip color="error" label={`Niezaliczone: ${checklistCoverage.failed}`} />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID scenariusza</TableCell>
                <TableCell>Scenariusz</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ostatnia aktualizacja</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scenarioChecklist.map((scenario) => (
                <TableRow key={scenario.id}>
                  <TableCell>{scenario.id}</TableCell>
                  <TableCell>{scenario.label}</TableCell>
                  <TableCell>
                    <Chip
                      color={getChecklistChipColor(scenario.status)}
                      label={checklistStatusLabel(scenario.status)}
                    />
                  </TableCell>
                  <TableCell>{scenario.lastUpdatedAtUtc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Scenariusz</TableCell>
                <TableCell>ID faktury</TableCell>
                <TableCell>ID korelacji</TableCell>
                <TableCell>Status końcowy</TableCell>
                <TableCell>Wynik</TableCell>
                <TableCell>Utworzono</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={`${entry.createdAtUtc}-${entry.scenarioId}`}>
                  <TableCell>{entry.scenarioId}</TableCell>
                  <TableCell>{entry.invoiceId || '-'}</TableCell>
                  <TableCell>{entry.correlationId || '-'}</TableCell>
                  <TableCell>{entry.finalStatus || '-'}</TableCell>
                  <TableCell>{passFailLabel(entry.passFail)}</TableCell>
                  <TableCell>{entry.createdAtUtc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
};

export default KsefOutgoingTestEvidencePanel;
